import { type ProtectedAtom } from '../types';

/**
 * Finds the protected atoms in `qMatch`.
 *
 * A protected atom is a span the engine must treat as one opaque operand: a balanced
 * quoted range, an approved whole URL, or a validated Atlassian identifier. Three rules
 * depend on getting this right:
 *
 * - `S-STRUCTURED` locks Search when the whole query is a single atom, and is the one
 *   place that validates a quoted range actually contains something.
 * - `navigationTarget` treats a non-empty atom after `open` as a navigation target.
 * - `compare` splits on a separator only outside every atom, so the `and` inside
 *   `"plan a and b"` is part of the operand.
 *
 * Quoted ranges are found first and every other pattern is matched only outside them, so
 * a URL inside a quoted title stays part of the title rather than becoming a second atom.
 */

/** Balanced double quotes: ASCII pairs and the curly pair. Single quotes are excluded because they are also apostrophes. */
const QUOTE_PAIRS: readonly [open: string, close: string][] = [
	['"', '"'],
	['“', '”'],
];

const ABSOLUTE_URL = /^https?:\/\/[^\s]+$/iu;

/**
 * A Confluence-relative content path.
 *
 * Anchored to the known `/wiki` prefix rather than any leading slash so an
 * ordinary query starting with `/` is not silently promoted to a Search lock.
 */
const WIKI_PATH = /^\/wiki\/[^\s]*$/u;

const ARI = /^ari:cloud:[a-z0-9-]+:[^\s:]*:[^\s]+$/iu;

/**
 * An explicitly labelled content identifier.
 *
 * A bare integer is deliberately **not** a validated content ID: nothing in the
 * browser can distinguish `12345` the page from `12345` the search term, and
 * treating it as structured input would lock Search on ordinary numeric queries.
 */
const CONTENT_ID = /^(?:contentId|pageId)=\d+$/iu;

const findQuotedAtoms = (qMatch: string): ProtectedAtom[] => {
	const atoms: ProtectedAtom[] = [];
	let index = 0;

	while (index < qMatch.length) {
		const pair = QUOTE_PAIRS.find(([open]) => qMatch.startsWith(open, index));

		if (!pair) {
			index += 1;
			continue;
		}

		const [open, close] = pair;
		const closeIndex = qMatch.indexOf(close, index + open.length);

		// An unbalanced quote is not an atom. Mid-typing input regularly has one,
		// and promoting it would lock Search on every partially typed phrase.
		if (closeIndex === -1) {
			index += 1;
			continue;
		}

		const end = closeIndex + close.length;

		atoms.push({
			kind: 'QUOTED',
			start: index,
			end,
			text: qMatch.slice(index, end),
		});

		index = end;
	}

	return atoms;
};

const classifyToken = (token: string): ProtectedAtom['kind'] | null => {
	if (ABSOLUTE_URL.test(token)) {
		return 'URL';
	}

	if (WIKI_PATH.test(token)) {
		return 'WIKI_PATH';
	}

	if (ARI.test(token)) {
		return 'ARI';
	}

	if (CONTENT_ID.test(token)) {
		return 'CONTENT_ID';
	}

	return null;
};

export const findProtectedAtoms = (qMatch: string): readonly ProtectedAtom[] => {
	const quoted = findQuotedAtoms(qMatch);
	const isInsideQuoted = (start: number, end: number): boolean =>
		quoted.some((atom) => start < atom.end && end > atom.start);

	const identifiers: ProtectedAtom[] = [];
	let offset = 0;

	for (const token of qMatch.split(' ')) {
		const start = offset;
		const end = offset + token.length;
		offset = end + 1;

		if (token.length === 0 || isInsideQuoted(start, end)) {
			continue;
		}

		const kind = classifyToken(token);

		if (kind) {
			identifiers.push({ kind, start, end, text: token });
		}
	}

	return Object.freeze([...quoted, ...identifiers].sort((left, right) => left.start - right.start));
};

/** Returns whether a single atom covers the whole query. */
export const isWholeQueryAtom = (
	qMatch: string,
	atoms: readonly ProtectedAtom[],
): ProtectedAtom | undefined =>
	atoms.length === 1 && atoms[0].start === 0 && atoms[0].end === qMatch.length
		? atoms[0]
		: undefined;
